import React, {useCallback, useMemo, useRef, useState} from "react";
import {Chatbot} from "@/model/Chatbot";
import toast from "react-hot-toast";
import {Input} from "@/components/ui/input";
import {Card, CardContent, CardFooter, CardHeader} from "@/components/ui/card";
import {cn} from "@/lib/utils";
import {Button} from "@/components/ui/button";
import {Send} from "lucide-react";
import {Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger} from "@/components/ui/sheet";
import moment from "moment/moment";
import {formatIsoReadable} from "@/utils/dateUtils";
import useWorkflowLog from "@/query/workflowLog/useWorkflowLog";
import {statusBadge} from "@/view/workflowLog/workflowLogList";
import {isAxiosError} from "@/error";
import WorkflowLogViewer from "@/view/workflowLog/component/WorkflowLogViewer";
import useProfile from "@/query/auth/useProfile";
import workflowRepository from "@/repository/WorkflowRepository";

interface QueryMessage {
    _id: string
    role: "user"
    content: string
}

interface AgentMessage {
    _id: string
    role: "agent"
    content: string
    resultId: string
    questionList?: string[]
}

const ChatSandbox: React.FC<{ chatbot: Chatbot }> = ({chatbot}) => {
    const {data: user} = useProfile({useErrorBoundary: false});
    const queryIndex = useRef(0)
    const queryRef = useRef<HTMLInputElement>(null);
    const sessionIdRef = useRef<string | undefined>(undefined);

    const [pending, setPending] = useState(false);
    const [messages, setMessages] = useState<(QueryMessage | AgentMessage)[]>([]);

    const defaultParams = useMemo(() => ({
        chatbot_id: chatbot._id,
        chatbot_name: chatbot.name,
        faq_cluster_id: chatbot.faqClusterId,
        rag_cluster_id: chatbot.ragClusterId,
    }), [chatbot])

    const onSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (pending || !user || !queryRef.current || !queryRef.current.value.trim())
            return

        const query = queryRef.current.value
        queryIndex.current += 1

        try {
            setPending(true)
            setMessages((prev) => [{
                _id: queryIndex.current.toString(),
                role: "user",
                content: query
            }, ...(prev)])
            queryRef.current.value = ""

            const {data} = await workflowRepository.run(
                user.workflowUserId,
                chatbot.chatHandler.workflowId,
                {...defaultParams, query},
                sessionIdRef.current
            );

            const result = data.result;
            sessionIdRef.current = result.sessionId

            setMessages((prev) => [{
                _id: result._id,
                role: "agent",
                content: result.result.answer,
                resultId: result._id,
                questionList: result.result.question_list ?? []
            }, ...(prev)])

        } catch (e) {
            if (isAxiosError(e) && e.response) {
                const data = e.response.data as any
                setMessages((prev) => [{
                    _id: data.result._id,
                    role: "agent",
                    content: data.result.answer,
                    resultId: data.result._id
                }, ...(prev)])
            } else {
                console.error(e)
                toast.error("에러")
            }

        } finally {
            setPending(false)
        }
    }, [chatbot.chatHandler.workflowId, defaultParams, pending, user])

    return (
        <Card className="h-full flex flex-col">
            <CardHeader className="flex flex-row items-center">
                <p className="text-sm font-medium leading-none">Chatbot '{chatbot.name}'</p>
            </CardHeader>
            <CardContent className="grow overflow-y-auto flex flex-col-reverse">
                {pending && (
                    <div
                        className="flex w-max max-w-[75%] rounded-lg p-3 px-4 text-sm mb-2 bg-muted text-muted-foreground">
                        Generating ...
                    </div>
                )}
                {messages.map((message) => {
                    if (message.role === "user") {
                        return (
                            <div
                                key={`query-${message._id}`}
                                className={cn(
                                    "flex w-max max-w-[75%] flex-col gap-2 rounded-lg px-3 py-2 text-sm mb-2",
                                    "ml-auto bg-primary text-primary-foreground"
                                )}
                            >
                                {message.content}
                            </div>
                        )
                    }
                    return (
                        <div key={`agent-${message._id}`} className="flex flex-col gap-2 mb-2">
                            <Sheet>
                                <SheetTrigger asChild>
                                    <div className="flex flex-col w-max max-w-[75%] cursor-pointer gap-2 rounded-lg px-3 py-2 text-sm bg-muted">
                                        {message.content}
                                    </div>
                                </SheetTrigger>
                                <LogSheetContent workflowResultId={message.resultId}/>
                            </Sheet>
                            {message.questionList && message.questionList.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 max-w-[75%]">
                                    {message.questionList.map((question, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => {
                                                if (queryRef.current) {
                                                    queryRef.current.value = question
                                                    queryRef.current.form?.requestSubmit()
                                                }
                                            }}
                                            className="text-xs p-3 rounded-full border border-border bg-background hover:bg-muted transition-colors"
                                        >
                                            {question}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )
                })}
            </CardContent>
            <CardFooter>
                <form
                    onSubmit={onSubmit}
                    className="flex gap-2 w-full items-center"
                >
                    <Input
                        id="message"
                        placeholder="Type your message..."
                        className="flex-1"
                        autoComplete="off"
                        ref={queryRef}
                        autoFocus={true}
                    />
                    <Button type="submit" size="icon" disabled={pending}>
                        <Send/>
                        <span className="sr-only">Send</span>
                    </Button>
                </form>
            </CardFooter>
        </Card>
    )
}

const LogSheetContent: React.FC<{ workflowResultId: string }> = ({workflowResultId}) => {
    const {data: log} = useWorkflowLog(workflowResultId)

    const defaultLayout = useMemo(() => {
        const layout = localStorage.getItem("react-resizable-panels:layout:workflow-log")
        return layout ? JSON.parse(layout) : undefined
    }, [])

    if (!log) return null

    const resultOutletHistory = log.operatorOutlets[log.resultOperatorId];
    const resultOutlet = Array.isArray(resultOutletHistory) ? resultOutletHistory.at(-1)! : resultOutletHistory;
    const executionTime = moment.utc(resultOutlet.endedAt).diff(moment.utc(log.createdAt), "milliseconds").toLocaleString("ko-KR");

    return (
        <SheetContent
            className="w-[90%] sm:w-[90%] sm:max-w-[90%] h-screen flex flex-col"
        >
            <SheetHeader className="flex">
                <SheetTitle className="flex items-center">
                    {statusBadge(log)}
                </SheetTitle>
                <SheetDescription>
                    <span className="text-xs text-slate-500 mr-5">
                        Duration (ms): {executionTime}
                    </span>
                    <span className="text-xs text-slate-500 mr-5">
                        Started At: {formatIsoReadable(log.startedAt)}
                    </span>
                    <span className="text-xs text-slate-500 mr-5">
                        Ended At: {formatIsoReadable(resultOutlet.endedAt)}
                    </span>
                </SheetDescription>
            </SheetHeader>

            <div className="rounded-xl border bg-card text-card-foreground shadow w-full h-full overflow-hidden">
                <WorkflowLogViewer
                    className="grow"
                    log={log}
                    defaultLayout={defaultLayout}
                />
            </div>
        </SheetContent>
    )
}

export default ChatSandbox